import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const ActiveBookingsTab = ({ onSwitchToTracking }) => {
  const { user } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState(null);  
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [showVsrModal, setShowVsrModal] = useState(false);
  const [vsrTarget, setVsrTarget] = useState(null);
  const [vsrForm, setVsrForm] = useState({ newVehicle: '', reason: 'breakdown' });
  const [vsrSubmitting, setVsrSubmitting] = useState(false);
  const [pendingVsrAssignmentIds, setPendingVsrAssignmentIds] = useState(new Set());
  const [pendingVsrBookingIds, setPendingVsrBookingIds] = useState(new Set());
  const [vsrExistsBookingIds, setVsrExistsBookingIds] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [preset, setPreset] = useState('till_date');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const userEmail = session?.user?.email || user?.email;
      const userId = session?.user?.id || user?.id;

      if (!userEmail && !userId) {
        setBookings([]);
        return;
      }

      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('id, factory_name, grade, quantity, rate_per_ton, total_amount, readable_offer_id, readable_booking_id, offer_id, status, booking_date, created_at, buyer_email, buyer_id, booking_type, no_of_trucks, loading_from, destination, assigned_vehicle_number, driver_mobile')
        ?.in('status', ['approved', 'confirmed', 'active', 'in_transit', 'do_generated', 'do_generated_partial', 'completed'])
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const filtered = (data || [])?.filter(b => {
        const isBuyer = b?.buyer_email === userEmail || b?.buyer_id === userId;
        if (!isBuyer) return false;
        // 4-day rule: in_transit older than 4 days → moved to Invoice Center
        if (b?.status === 'in_transit') {
          const cutoff = new Date();
          cutoff.setDate(cutoff.getDate() - 4);
          if (new Date(b?.created_at) < cutoff) return false;
        }
        return true;
      });

      const bookingIds = filtered?.map(b => b?.id);
      let assignmentsByBooking = {};
      if (bookingIds?.length > 0) {
        const { data: assignments } = await supabase
          ?.from('order_truck_assignments')
          ?.select('*')
          ?.in('booking_id', bookingIds)
          ?.order('assigned_at', { ascending: true });
        (assignments || [])?.forEach(a => {
          if (!assignmentsByBooking?.[a?.booking_id]) assignmentsByBooking[a?.booking_id] = [];
          assignmentsByBooking?.[a?.booking_id]?.push(a);
        });
      }

      let pendingVsrIds = new Set();
      let pendingVsrBookingIds = new Set();
      let vsrExistsBookingIds = new Set();
      if (bookingIds?.length > 0) {
        const { data: vsrs } = await supabase
          ?.from('vehicle_substitution_requests')
          ?.select('assignment_id, booking_id, status')
          ?.in('booking_id', bookingIds);
        (vsrs || [])?.forEach(v => {
          if (v?.assignment_id) pendingVsrIds.add(v.assignment_id);
          if (v?.booking_id) vsrExistsBookingIds.add(v.booking_id);
          if (v?.booking_id && v?.status === 'pending') pendingVsrBookingIds.add(v.booking_id);
        });
      }
      setPendingVsrAssignmentIds(pendingVsrIds);
      setPendingVsrBookingIds(pendingVsrBookingIds);
      setVsrExistsBookingIds(vsrExistsBookingIds);

      const supabaseBookings = filtered?.map(b => ({
        id: b?.id,
        readable_offer_id: b?.readable_offer_id,
        readable_booking_id: b?.readable_booking_id,
        readableOfferId: b?.readable_offer_id,
        readableBookingId: b?.readable_booking_id,
        offerId: b?.offer_id || null,
        offer_id: b?.offer_id || null,
        factory_name: b?.factory_name || '—',
        millName: b?.factory_name || '—',
        grade: b?.grade || '—',
        quantity: Number(b?.quantity) || 0,
        rate: Number(b?.rate_per_ton) || 0,
        totalAmount: Number(b?.total_amount) || 0,
        total_amount: Number(b?.total_amount) || 0,
        status: b?.status || 'Processing',
        deliveryFrom: b?.loading_from || '—',
        deliveryTo: b?.destination || '—',
        date: b?.booking_date || b?.created_at || new Date()?.toISOString(),
        booking_date: b?.booking_date || null,
        created_at: b?.created_at || null,
        bookingType: b?.booking_type || 'sugar_only',
        noOfTrucks: b?.no_of_trucks || 0,
        assignedVehicleNumber: b?.assigned_vehicle_number || '',
        driverMobile: b?.driver_mobile || '',
        truckAssignments: assignmentsByBooking?.[b?.id] || [],
        _source: 'supabase',
      }));

      setBookings(supabaseBookings || []);
    } catch (err) {
      console.error('ActiveBookingsTab error:', err?.message);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadBookings();

    const channel = supabase?.channel('active_bookings_tab')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'buyer_bookings' }, () => loadBookings())
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'order_truck_assignments' }, () => loadBookings())
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'vehicle_substitution_requests' }, () => loadBookings())
      ?.subscribe();
    
    return () => { supabase?.removeChannel(channel); };
  }, [loadBookings]);

  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredBookings = bookings.filter(b => {
    const text = searchText.toLowerCase();
    if (text && ![
      b.readable_booking_id, b.assignedVehicleNumber,
      b.factory_name, b.grade, b.deliveryTo
    ].some(f => (f || '').toLowerCase().includes(text))) return false;
    if (statusFilter !== 'all' && b.status !== statusFilter) return false;
    if (dateFrom && new Date(b.created_at) < dateFrom) return false;
    if (dateTo && new Date(b.created_at) > dateTo) return false;
    return true;
  });

  const toggleExpanded = (bookingId) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next?.has(bookingId)) next?.delete(bookingId);
      else next?.add(bookingId);
      return next;
    });
  };

  const handleTrackShipment = (booking) => {
    onSwitchToTracking(booking?.id);
  };

  const handleEditVehicle = (booking, assignment) => {
    setVsrTarget({ booking, assignment });
    setVsrForm({ newVehicle: '', reason: 'breakdown' });
    setShowVsrModal(true);
  };

  const handleVsrSubmit = async () => {
    if (!vsrForm?.newVehicle?.trim()) {
      setNotification({ show: true, message: 'Please enter the new vehicle number.' });
      setTimeout(() => setNotification({ show: false, message: '' }), 3000);
      return;
    }
    setVsrSubmitting(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const userId = session?.user?.id || user?.id;

      const { error } = await supabase
        ?.from('vehicle_substitution_requests')
        ?.insert({
          booking_id: vsrTarget?.booking?.id,
          assignment_id: vsrTarget?.assignment?.id,
          readable_booking_id: vsrTarget?.booking?.readable_booking_id,
          old_vehicle_number: (vsrTarget?.assignment?.vehicle_number || '').toUpperCase(),
          new_vehicle_number: vsrForm?.newVehicle?.trim()?.toUpperCase(),
          reason: vsrForm?.reason,
          status: 'pending',
          requested_by_user_id: userId,
          requested_via: 'pwa',
        });

      if (error) throw error;

      setShowVsrModal(false);
      setNotification({ show: true, message: 'Vehicle change request submitted. Awaiting approval.' });
      setTimeout(() => setNotification({ show: false, message: '' }), 4000);
      loadBookings();
    } catch (err) {
      setNotification({ show: true, message: `Failed to submit: ${err?.message}` });
      setTimeout(() => setNotification({ show: false, message: '' }), 4000);
    } finally {
      setVsrSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const styleMap = {
      approved: 'bg-green-100 text-green-700',
      confirmed: 'bg-blue-100 text-blue-700',
      active: 'bg-emerald-100 text-emerald-700',
      in_transit: 'bg-indigo-100 text-indigo-700',
      do_generated: 'bg-purple-100 text-purple-700',
      do_generated_partial: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-gray-100 text-gray-600',
    };
    const labelMap = {
      approved: '✅ Approved',
      confirmed: '🤝 Confirmed',
      active: '🟢 Active',
      in_transit: '🚛 In Transit',
      do_generated: '📄 DO Generated',
      do_generated_partial: '📄 DO Partial',
      completed: '✔ Completed',
    };
    return (
      <span className={`px-3 py-0.5 text-xs font-semibold rounded-full ${styleMap?.[status] || 'bg-gray-100 text-gray-500'}`}>
        {labelMap?.[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification?.show && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{ background: '#1e4a72', border: '1px solid #2d6aa0' }}>
          <Icon name="CheckCircle" size={18} color="#4ade80" />
          {notification?.message}
        </div>
      )}

      {/* VSR Modal */}
      {showVsrModal && vsrTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">Request Vehicle Change</h3>
            <p className="text-xs text-gray-500 mb-5">
              Submit a request to substitute the assigned vehicle. Approval required before the change takes effect.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Booking Ref</label>
                <div className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 font-mono text-sm text-gray-700">
                  {vsrTarget?.booking?.readable_booking_id || '—'}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">Current Vehicle</label>
                <div className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 font-mono text-sm text-gray-700">
                  {vsrTarget?.assignment?.vehicle_number || '—'}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  New Vehicle Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={vsrForm?.newVehicle}
                  onChange={e => setVsrForm(f => ({ ...f, newVehicle: e.target.value.toUpperCase() }))}
                  placeholder="e.g. MH12AB5678"
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">
                  Reason <span className="text-red-500">*</span>
                </label>
                <select
                  value={vsrForm?.reason}
                  onChange={e => setVsrForm(f => ({ ...f, reason: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="breakdown">Breakdown</option>
                  <option value="no_show">No Show</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowVsrModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleVsrSubmit}
                disabled={vsrSubmitting}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
              >
                {vsrSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Active Bookings</h2>
          <p className="text-sm mt-0.5 text-gray-500">
            {loading ? 'Loading...' : `${filteredBookings?.length} booking${filteredBookings?.length !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <button
          onClick={loadBookings}
          className="self-start sm:self-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
        >
          <Icon name="RefreshCw" size={13} />
          Refresh
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col gap-2">
        <input
          type="text"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search by Booking ID, vehicle, factory, grade, destination..."
          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <option value="all">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="do_generated">DO Generated</option>
          <option value="do_generated_partial">DO Partial</option>
          <option value="in_transit">In Transit</option>
        </select>
        <DateRangeFilter
          preset={preset} customFrom={customFrom} customTo={customTo}
          onPresetChange={setPreset} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl bg-amber-50 border border-amber-100">
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400">Loading bookings...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredBookings?.length === 0 && (
        <div className="text-center py-16 rounded-xl bg-amber-50 border border-amber-100">
          <Icon name="ShoppingCart" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium text-gray-400">No active bookings found</p>
          <p className="text-xs mt-1 text-amber-400">Your confirmed sugar bookings will appear here</p>
        </div>
      )}

      {/* Booking Cards */}
      {!loading && filteredBookings?.length > 0 && (
        <div className="space-y-4">
          {filteredBookings?.map((booking) => {
            const isExpanded = expandedCards?.has(booking?.id);
            const assignments = booking?.truckAssignments || [];
            const hasMultipleVehicles = assignments?.length > 1;
            const canTrackMain = !hasMultipleVehicles && (
              booking?.status === 'in_transit' ||
              booking?.status === 'active' ||
              booking?.status === 'confirmed'
            );
          const canEdit = ['approved', 'confirmed', 'active', 'do_generated', 'do_generated_partial'].includes(booking?.status);            

            return (
              <div
                key={booking?.id}
                className="border border-gray-200 rounded-3xl bg-white shadow-sm hover:shadow-md transition-all overflow-hidden"
              >
                {/* Card Body */}
                <div className="p-4 md:p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0 text-2xl">
                      🏭
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg md:text-xl leading-tight text-gray-900">
                        {booking?.factory_name}
                      </h3>

                      <div className="mt-1.5">
                        <div className="text-sm font-medium text-gray-900">
                          Order Ref: <span className="font-mono font-semibold">{booking?.readable_booking_id || booking?.readable_offer_id || booking?.offer_id || 'N/A'}</span>
                        </div>
                      </div>

                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        {new Date(booking?.booking_date || booking?.created_at || booking?.date)?.toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </p>

                      <div className="flex flex-wrap gap-2 mt-3">
                        {statusBadge(booking?.status)}
                      </div>

                      <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-5 text-sm">
                        <div>
                          <p className="text-xs text-gray-500">Grade</p>
                          <p className="font-medium">{booking?.grade || 'S1-30'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Quantity</p>
                          <p className="font-medium">{booking?.quantity} MT</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="font-medium text-emerald-600">
                            ₹{Number(booking?.total_amount || 0)?.toLocaleString('en-IN')}
                          </p>
                        </div>
                        {booking?.deliveryFrom && booking?.deliveryFrom !== '—' && (
                          <div>
                            <p className="text-xs text-gray-500">From</p>
                            <p className="font-medium">{booking?.deliveryFrom}</p>
                          </div>
                        )}
                        {booking?.deliveryTo && booking?.deliveryTo !== '—' && (
                          <div>
                            <p className="text-xs text-gray-500">To</p>
                            <p className="font-medium">{booking?.deliveryTo}</p>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 mt-6">
                        {assignments?.length > 0 && (
                          <button
                            onClick={() => toggleExpanded(booking?.id)}
                            className={`flex-1 font-medium py-3 rounded-2xl flex items-center justify-center gap-2 transition-all border ${
                              isExpanded
                                ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                                : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
                            {isExpanded ? 'Hide Details' : `Details (${assignments?.length} truck${assignments?.length !== 1 ? 's' : ''})`}
                          </button>
                        )}
                        {assignments?.length === 0 && booking?.assignedVehicleNumber && (
                          <div className="w-full mt-2 px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 text-sm">
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <p className="text-xs text-gray-500">Vehicle</p>
                                <p className="font-mono font-semibold text-gray-800">{booking?.assignedVehicleNumber}</p>
                                {booking?.driverMobile && (
                                  <p className="text-xs text-gray-500">Driver: {booking?.driverMobile}</p>
                                )}
                              </div>
                              {vsrExistsBookingIds?.has(booking?.id) ? (
                                pendingVsrBookingIds?.has(booking?.id) ? (
                                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                    🕐 Request Pending
                                  </span>
                                ) : (
                                  <button
                                    disabled
                                    className="flex items-center gap-1 px-2.5 py-1 bg-gray-200 text-gray-400 text-xs font-medium rounded-lg whitespace-nowrap cursor-not-allowed"
                                  >
                                    <Icon name="Edit2" size={11} />
                                    Vehicle Changed
                                  </button>
                                )
                              ) : (
                                <button
                                  onClick={() => handleEditVehicle(booking, { vehicle_number: booking?.assignedVehicleNumber, id: null })}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
                                >
                                  <Icon name="Edit2" size={11} />
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        {assignments?.length === 0 && !booking?.assignedVehicleNumber && booking?.status === 'approved' && (
                          <span className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-medium border border-amber-200 bg-amber-50 text-amber-700">
                            <Icon name="Clock" size={15} color="#d97706" />
                            Trucks pending assignment
                          </span>
                        )}

<button
                          onClick={() => booking?.status === 'in_transit' && onSwitchToTracking(booking?.id)}
                          disabled={booking?.status !== 'in_transit'}
                          className={`w-full font-medium py-3 rounded-2xl flex items-center justify-center gap-2 transition-colors ${
                            booking?.status === 'in_transit'
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                          }`}
                        >
                          🚛 Track Truck
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Assigned Vehicles — collapsible */}
                {assignments?.length > 0 && (
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="border-t border-gray-100 bg-gray-50 px-4 md:px-6 py-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Icon name="Truck" size={15} color="#6b7280" />
                        Assigned Vehicles
                        <span className="ml-1 px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                          {assignments?.length}
                        </span>
                      </h4>
                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-white border-b border-gray-100">
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">#</th>
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Vehicle No.</th>
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Driver</th>
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Qty (MT)</th>
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Rate/Ton</th>
                              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Status</th>
                              {hasMultipleVehicles && (
                                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Track</th>
                              )}
                              {canEdit && (
                                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">Action</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {assignments?.map((a, idx) => {
                              const hasPendingVsr = pendingVsrAssignmentIds?.has(a?.id);
                              return (
                                <tr key={a?.id || idx} className="border-b border-gray-50 last:border-0 hover:bg-white transition-colors">
                                  <td className="px-3 py-2.5 text-gray-400 text-xs">{idx + 1}</td>
                                  <td className="px-3 py-2.5 font-mono font-semibold text-gray-800 text-xs whitespace-nowrap">
                                    {a?.vehicle_number || '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-gray-600 text-xs whitespace-nowrap">
                                    <div>{a?.driver_name || '—'}</div>
                                    {a?.driver_phone && (
                                      <div className="text-gray-400 text-xs">{a?.driver_phone}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5 text-gray-700 text-xs">{a?.assigned_quantity || '—'}</td>
                                  <td className="px-3 py-2.5 text-gray-700 text-xs">
                                    {a?.rate_per_ton ? `₹${Number(a?.rate_per_ton)?.toLocaleString('en-IN')}` : '—'}
                                  </td>
                                  <td className="px-3 py-2.5 text-xs">
                                    <span className={`px-2 py-0.5 rounded-full font-medium ${
                                      a?.status === 'in_transit' ? 'bg-indigo-100 text-indigo-700' :
                                      a?.status === 'delivered' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {a?.status || 'assigned'}
                                    </span>
                                  </td>
                                  {hasMultipleVehicles && (
                                    <td className="px-3 py-2.5">
                                      <button
                                        onClick={() => handleTrackShipment(booking, a)}
                                        className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
                                      >
                                        <Icon name="MapPin" size={11} />
                                        Track
                                      </button>
                                    </td>
                                  )}
                                  {canEdit && (
                                    <td className="px-3 py-2.5">
                                      {hasPendingVsr ? (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                          🕐 Pending
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => handleEditVehicle(booking, a)}
                                          className="flex items-center gap-1 px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
                                        >
                                          <Icon name="Edit2" size={11} />
                                          Edit
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActiveBookingsTab;

